import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import moment from 'moment-timezone';

@Injectable({
  providedIn: 'root',
})
export class SharedService {

  readonly TREE_KEYS = 'treeData';
  private treeDataSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  selectedNodes = signal<{ [key: string]: any }>({});
  private mapLocation = signal<any | []>([]);
  tableData = signal<any[]>([]);
  tableTitle = signal<string>('');

  // ── Tab state — used by ReportsLayout + child components ──────────────────
  selectedTab = signal<string>('report');

  // ── Date range state (single source of truth for chart ↔ report-data sync) ─
  dailyPerfDateRange   = signal<any>(null);                     // daily-performance
  profileAnalysisDateRange = signal<any>(null);                 // profile-analysis tabs
  billingHistStartMonth = signal<string>('');                   // billing-history start
  billingHistEndMonth   = signal<string>('');                   // billing-history end
  monthlyBillDate       = signal<{ month: string; year: string } | null>(null); // monthly-bill

  setDailyPerfDateRange(range: any): void { this.dailyPerfDateRange.set(range); }
  setProfileAnalysisDateRange(range: any): void { this.profileAnalysisDateRange.set(range); }
  setBillingHistStartMonth(m: string): void { this.billingHistStartMonth.set(m); }
  setBillingHistEndMonth(m: string): void { this.billingHistEndMonth.set(m); }
  setMonthlyBillDate(d: { month: string; year: string }): void { this.monthlyBillDate.set(d); }

  // ── Reload request: report-data → chart component ─────────────────────────
  private _reloadRequest = new Subject<{ type: string; params: any }>();
  reloadRequest$ = this._reloadRequest.asObservable();

  // ── Loading state for report-data tab ─────────────────────────────────────
  reportDataLoading = signal<boolean>(false);
  setReportDataLoading(v: boolean): void { this.reportDataLoading.set(v); }

  requestReload(type: string, params: any): void {
    this._reloadRequest.next({ type, params });
  }

  setSelectedTab(tab: string): void {
    this.selectedTab.set(tab);
  }
  // ─────────────────────────────────────────────────────────────────────────

  readonly siteCoordinatesMap: Record<string, { lat: number; lng: number }> = {
    "MovieWorld": { lat: -27.903155, lng: 153.315003 },
    "Wet 'n Wild G.C.": { lat: -27.914518, lng: 153.318358 },
    "Nara Resort": { lat: -27.961209, lng: 153.426557 },
    "Sea World": { lat: -27.9559, lng: 153.4257 },
    "Wet 'n Wild Sydney": { lat: -33.807148, lng: 150.910480 }
  }

  constructor() { }

  setTreeData(data: any): void {
    this.treeDataSubject.next(data);
    sessionStorage.setItem(this.TREE_KEYS, JSON.stringify(data));
  }

  getTreeData(): any {
    const currentData = this.treeDataSubject.getValue();
    if (currentData) return currentData;
    const storedData = sessionStorage.getItem(this.TREE_KEYS);
    return storedData ? JSON.parse(storedData) : null;
  }

  setSelectedNode(routeKey: string, node: any) {
    this.selectedNodes.update(prev => ({ ...prev, [routeKey]: node }));
  }

  getSelectedNode(key: string) {
    return this.selectedNodes()[key];
  }

  setMapLocation(location: any) {
    this.mapLocation.set(location);
  }

  getMapLocation(): any {
    return this.mapLocation();
  }

  setTableData(data: any[]) {
    this.tableData.set(data);
  }

  setTableTitle(title: string) {
    this.tableTitle.set(title);
  }

  getTableTitle(): string {
    return this.tableTitle();
  }
}